import React from 'react';
import { connect } from 'react-redux';
import gql from 'graphql-tag';
import styled from '@emotion/styled';
import { ThemeProvider } from 'emotion-theming';
import { Grid, Row, Col } from 'react-styled-flexboxgrid';
import { useQuery } from '@apollo/react-hooks';

import editable from '../assets/LandingPages/Home';

// Common components...
import SimpleSlat from '../components/Slats/SimpleSlat';
import Seo from '../components/Common/Seo';
import Heading from '../components/Common/Heading';
import Dek from '../components/Common/Dek';
import NavButton from '../components/LandingPage/Home/NavButton';
import Button from '../components/Common/Button';
import VideoBackgroundContainer from '../components/LandingPage/Common/VideoBackgroundContainer';

// Landing Page Specific components...
import PlatformContentSwitcher from '../components/LandingPage/Common/PlatformContentSwitcher';
import FullStretchSlat from '../components/LandingPage/Home/FullStretchSlat';
import Comparison from '../components/LandingPage/Common/Comparison';
import FlyerCategoryTiles from '../components/LandingPage/Home/FlyerCategoryTiles';
import OffsetCategoryMosaic from '../components/LandingPage/Home/OffsetCategoryMosaic';
import CategoryBlocks from '../components/LandingPage/Common/CategoryBlocks';
import BlogPosts from '../components/LandingPage/Common/BlogPosts';

// Styling...
import colors from '../styling/colors';
import LandingPageThemes from '../styling/themes/LandingPageThemes';
import {
  paragraphXSmall,
  paragraph,
  mediumWeight,
  trackedStyles,
  BasisFont,
  PlantinFont,
} from '../styling/mixins/typography';
import {
  DESKTOP_LARGE_START_QUERY,
  DESKTOP_ABOVE_MAX_QUERY,
  LANDING_PAGE_MAX_WIDTH,
  LARGE_MAX_BREAKPOINT,
} from '../styling/mixins/responsive';
import getPageSizeLayoutTheme from '../styling/themes/getPageSizeLayoutTheme';

// Utils...
import { canonicalUrl } from '../utils/selectors/seo';

const GridContainer = styled(Grid)`
  margin: auto;
  overflow-x: hidden;
  max-width: ${LANDING_PAGE_MAX_WIDTH}px;
`;

const SlatHeading2 = styled(Heading.h2)`
  ${mediumWeight};
`;

const SlatHeading3 = styled(Heading.h3)`
  ${mediumWeight};
`;

const SectionHeading = styled(SlatHeading2)`
  margin: 4.5rem 4rem 1.5rem;
  text-align: center;
`;

const HomeHeading = styled(Heading.h2)`
  ${mediumWeight};
  text-align: center;
`;

const MobileHeading = styled(Heading.h1)`
  ${PlantinFont};
  color: white;
  text-align: center;
  width: 100%;
  height: 20%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
`;

const Title = styled(Heading.h2)`
  ${BasisFont};
  font-weight: 700;
  font-style: normal;
  text-align: center;
  letter-spacing: -0.2px;
  margin: 5rem 0 2rem;

  @media (${DESKTOP_LARGE_START_QUERY}) {
    font-size: 2rem;
  }

  @media (${DESKTOP_ABOVE_MAX_QUERY}) {
    font-size: 2.5rem;
    letter-spacing: -0.3px;
  }
`;

const FlyerCategoryLabel = styled(Heading.h3)`
  ${trackedStyles};
  padding: 0.5rem;
`;

const HomeDek = styled.p`
  ${paragraph};
  color: ${colors('boulder')};
  margin-bottom: 1rem;
  padding: 0 2rem;
  text-align: center;
`;

const FlyerCategories = styled.div`
  text-align: center;
  margin: 0.75rem 0 4rem;
`;

const HeroTitle = styled.h1`
  ${PlantinFont}
  letter-spacing: -1px;
  font-weight: 500;
  font-size: 2.5rem;

  @media (${DESKTOP_LARGE_START_QUERY}) {
    font-size: 3rem;
  }

  @media (min-width: ${LARGE_MAX_BREAKPOINT}px) {
    font-size: 3.5rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-evenly;
`;

// Mobile Hero Slat Specific Styles
const HeroSlat = styled(SimpleSlat)`
  margin: 0;
`;

const PaddedPaperSourceSlat = styled(SimpleSlat)`
  margin-top: 3rem;
`;

const MobileHeroContainer = styled.div`
  margin-bottom: 2rem;
`;

const Header = styled.div`
  width: 100%;
  height: 20%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
`;

const getButtonId = position => `HP button ${position}`;

const QUERY = gql`
  query getQuery($tags: [String]!) {
    blogPosts(tags: $tags) {
      embedded
      id
      link
      title
    }
  }
`;

const {
  hero,
  desktopHero,
  desktopHeroSecondary,
  flyerCategories,
  mobileHeroSlat1,
  mobileHeroSlat2,
  mobileHeroSlat3,
  offsetMosaic,
  paperSource,
  seasonal,
  seo,
  uploadYourOwn,
  designCollectionHeading,
  designerCollections,
  blog,
} = editable;

const Home = ({ layoutSize, isMobile }) => {
  const pageTheme = LandingPageThemes.default;
  const pageSizeLayoutTheme = getPageSizeLayoutTheme({
    layoutSize,
    isLandingPage: true,
  });

  const theme = {
    ...pageSizeLayoutTheme,
    ...pageTheme,
  };

  const mobileHeroContent = (
    <MobileHeroContainer>
      <VideoBackgroundContainer media={mobileHeroSlat1.backgroundVideo}>
        <Header>
          <MobileHeading>{mobileHeroSlat1.heading}</MobileHeading>
          <Button {...mobileHeroSlat1.button} />
        </Header>
        <HeroSlat {...mobileHeroSlat1.slatProps}></HeroSlat>
      </VideoBackgroundContainer>
      <FlyerCategories>
        <FlyerCategoryLabel>Popular Flyer Categories</FlyerCategoryLabel>
        <FlyerCategoryTiles {...flyerCategories} />
      </FlyerCategories>
      <HomeHeading>{mobileHeroSlat2.heading}</HomeHeading>
      <HomeDek>{mobileHeroSlat2.dek}</HomeDek>
      <HeroSlat media={mobileHeroSlat2.media} {...mobileHeroSlat2.slatProps}>
        <Button {...mobileHeroSlat2.button} />
      </HeroSlat>
      <HeroSlat media={mobileHeroSlat3.media} {...mobileHeroSlat3.slatProps}>
        <HomeHeading>{mobileHeroSlat3.heading}</HomeHeading>
        <HomeDek>{mobileHeroSlat3.dek}</HomeDek>
        <Button {...mobileHeroSlat3.button} />
      </HeroSlat>
    </MobileHeroContainer>
  );

  const desktopHeroContent = (
    <div>
      <FullStretchSlat {...desktopHero} tracking={desktopHero.tracking}>
        <HeroTitle style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
          {desktopHero.title}
        </HeroTitle>
        <ButtonContainer>
          <NavButton {...desktopHero.button1} identifier={getButtonId(1)} />
          <NavButton {...desktopHero.button2} identifier={getButtonId(2)} />
          <NavButton {...desktopHero.button3} identifier={getButtonId(3)} />
          <NavButton {...desktopHero.button4} identifier={getButtonId(4)} />
          <NavButton {...desktopHero.button5} identifier={getButtonId(5)} />
        </ButtonContainer>
      </FullStretchSlat>
      <Title>{desktopHeroSecondary.title}</Title>
      <Comparison
        withBorderOnHover={desktopHeroSecondary.withBorderOnHover}
        cards={desktopHeroSecondary.cards}
        flyer={desktopHeroSecondary.flyer}
        isMobile={isMobile}
        tracking={desktopHeroSecondary.tracking}
      />
    </div>
  );

  const { data = {} } = useQuery(QUERY, {
    variables: { tags: blog.tags },
  });

  return (
    <ThemeProvider theme={theme}>
      <GridContainer fluid={true}>
        <Row>
          <Col xs={12}>
            <Seo {...seo} canonicalUrl="" />
            <PlatformContentSwitcher
              tracking={hero.tracking}
              mobile={mobileHeroContent}
              desktop={desktopHeroContent}
            />
            <OffsetCategoryMosaic
              isMobile={isMobile}
              {...offsetMosaic}
              tracking={offsetMosaic.tracking}
            />
            <SimpleSlat
              media={seasonal.media}
              {...seasonal.slatProps}
              tracking={seasonal.tracking}
            >
              <SlatHeading3 {...seasonal.h3} />
              <Button {...seasonal.button} />
            </SimpleSlat>
            <SimpleSlat
              media={uploadYourOwn.media}
              {...uploadYourOwn.slatProps}
              tracking={uploadYourOwn.tracking}
            >
              <SlatHeading2>{uploadYourOwn.h2}</SlatHeading2>
              <Dek>{uploadYourOwn.dek}</Dek>
              <Button {...uploadYourOwn.button} />
            </SimpleSlat>
            <SectionHeading>{designCollectionHeading}</SectionHeading>
            <CategoryBlocks
              {...designerCollections}
              tracking={designerCollections.tracking}
            />
            <PaddedPaperSourceSlat
              media={paperSource.media}
              {...paperSource.slatProps}
              tracking={paperSource.tracking}
            >
              <SlatHeading2>{paperSource.h2}</SlatHeading2>
              <Dek>{paperSource.dek}</Dek>
              <Button {...paperSource.button} />
            </PaddedPaperSourceSlat>
            <BlogPosts
              posts={data.blogPosts}
              title="See what's inspiring us:"
              tracking={blog.tracking}
            />
          </Col>
        </Row>
      </GridContainer>
    </ThemeProvider>
  );
};

// May need to add const mapDispatchToProps = { requestPosts }; in to dispatch if needed...
const mapStateToProps = ({ env: { layoutSize, isMobile } }) => ({
  layoutSize,
  isMobile,
});

export default connect(mapStateToProps)(Home);
